import React, { useEffect, useState } from 'react';
import { Calendar, Package, Settings, HelpCircle, ChevronRight, Shield, Star, Edit2, LogOut, CheckCircle2, Heart, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../components/Badge';
import { Logo } from '../components/Logo';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { AvatarSelector } from '../components/AvatarSelector';
import { SegmentedControl } from '../components/SegmentedControl';
import { Rating } from '../components/Rating';
import { User, updateUserProfile, getUserBio } from '../utils/auth';
import { mockListings } from '../utils/mockListings';
// Character limits based on industry best practices
const NAME_MAX_LENGTH = 50;
const BIO_MAX_LENGTH = 160;
interface ProfileProps {
  currentUser?: User | null;
  onProfileUpdate?: (user: User) => void;
  onCreateListing: () => void;
  onItemClick: (id: string) => void;
  onNavigateToBookings: () => void;
  onNavigateToInventory: () => void;
  onNavigateToSettings: () => void;
  onNavigateToSupport: () => void;
  onLogout: () => void;
  initialTab?: string;
}
export function Profile({
  currentUser,
  onProfileUpdate,
  onCreateListing,
  onItemClick,
  onNavigateToBookings,
  onNavigateToInventory,
  onNavigateToSettings,
  onNavigateToSupport,
  onLogout,
  initialTab = 'Listings'
}: ProfileProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  // Profile state
  const [profile, setProfile] = useState({
    name: currentUser?.name || 'Oliver M.',
    avatar: currentUser?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80',
    bio: getUserBio() || 'Sharing tools and gear with my neighbors. Happy to help with any questions!',
    verified: currentUser?.verified || {
      phone: true,
      id: true
    },
    stats: {
      asRenter: {
        rating: 4.9,
        reviews: 32
      },
      asLender: {
        rating: 4.8,
        reviews: 28
      },
      bookings: 180
    }
  });
  // Mock listings for this user (using first 4 from mock data)
  const myListings = mockListings.slice(0, 4);
  // Mock reviews
  const myReviews = [{
    id: 'r1',
    author: 'Sarah K.',
    rating: 5,
    date: '2 weeks ago',
    text: 'Oliver was great to communicate with. The drill was in perfect condition.'
  }, {
    id: 'r2',
    author: 'Mike R.',
    rating: 5,
    date: '1 month ago',
    text: 'Super helpful and flexible with pickup/dropoff times.'
  }, {
    id: 'r3',
    author: 'Jessica T.',
    rating: 4,
    date: '2 months ago',
    text: 'Good experience overall. Item worked as expected.'
  }];
  const isFullyVerified = profile.verified.phone && profile.verified.id;
  useEffect(() => {
    if (currentUser) {
      setProfile(prev => ({
        ...prev,
        name: currentUser.name,
        avatar: currentUser.avatar || prev.avatar,
        verified: currentUser.verified,
        bio: getUserBio() || prev.bio
      }));
    }
  }, [currentUser]);
  const [editForm, setEditForm] = useState({
    ...profile
  });
  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const result = await updateUserProfile({
        name: editForm.name.slice(0, NAME_MAX_LENGTH),
        avatar: editForm.avatar,
        bio: editForm.bio.slice(0, BIO_MAX_LENGTH),
        verified: editForm.verified
      });
      if (result.success && result.user) {
        setProfile({
          ...profile,
          name: editForm.name.slice(0, NAME_MAX_LENGTH),
          avatar: editForm.avatar,
          bio: editForm.bio.slice(0, BIO_MAX_LENGTH),
          verified: editForm.verified
        });
        if (onProfileUpdate) {
          onProfileUpdate(result.user);
        }
        setShowEditModal(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const menuItems = [{
    id: 'inventory',
    label: 'Manage Inventory',
    icon: Package,
    onClick: onNavigateToInventory
  }, {
    id: 'bookings',
    label: 'Booking History',
    icon: Calendar,
    onClick: onNavigateToBookings
  }, {
    id: 'settings',
    label: 'Account Settings',
    icon: Settings,
    onClick: onNavigateToSettings
  }, {
    id: 'support',
    label: 'Support Center',
    icon: HelpCircle,
    onClick: onNavigateToSupport
  }, {
    id: 'logout',
    label: 'Log Out',
    icon: LogOut,
    onClick: onLogout,
    variant: 'danger' as const
  }];
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1383">
      {/* Header */}
      <div className="bg-white border-b border-sand sticky top-0 z-20" data-id="element-1384">
        <div className="max-w-md mx-auto px-4 pt-4 pb-3" data-id="element-1385">
          <Logo variant="full" size="md" data-id="element-1386" />
        </div>
      </div>

      <div className="max-w-md mx-auto" data-id="element-1387">
        {/* Profile Card Section */}
        <div className="bg-white p-3 relative" style={{
        minHeight: '140px'
      }} data-id="element-1388">
          {/* Edit Button (Top Right) */}
          <button onClick={() => {
          setEditForm({
            ...profile
          });
          setShowEditModal(true);
        }} className="absolute top-3 right-3 p-1.5 text-moss-gray hover:text-dark-green transition-colors z-10" data-id="element-1389">
            <Edit2 size={20} data-id="element-1390" />
          </button>

          {/* Heart Button (Bottom Left) */}
          <button className="absolute bottom-3 left-3 p-1.5 text-moss-gray hover:text-red-500 transition-colors z-10" data-id="element-1391">
            <Heart size={20} data-id="element-1392" />
          </button>

          <div className="flex items-start gap-3" data-id="element-1393">
            {/* Avatar */}
            <div className="relative flex-shrink-0 ml-1 mt-1" data-id="element-1394">
              <img src={profile.avatar} alt={profile.name} className="w-[60px] h-[60px] rounded-full object-cover border border-sand" data-id="element-1395" />
            </div>

            {/* Info */}
            <div className="flex-1 pt-1" data-id="element-1396">
              <div className="flex items-center gap-1" data-id="element-1397">
                <h2 className="text-sm font-bold text-charcoal" data-id="element-1398">
                  {profile.name}
                </h2>
                {isFullyVerified && <div className="text-dark-green" data-id="element-1399">
                    <Shield size={16} className="fill-dark-green text-white" data-id="element-1400" />
                  </div>}
              </div>

              <p className="text-xs text-moss-gray mt-1" data-id="element-1401">
                {profile.stats.bookings} total bookings
              </p>

              <p className="text-[11px] text-charcoal/80 mt-2 leading-tight line-clamp-2 pr-6" data-id="element-1402">
                {profile.bio}
              </p>

              {/* Ratings */}
              <div className="mt-2 space-y-0.5" data-id="element-1403">
                <div className="flex items-center gap-1" data-id="element-1404">
                  <div className="flex" data-id="element-1405">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} className="fill-clay text-clay" data-id="element-1406" />)}
                  </div>
                  <span className="text-[11px] text-moss-gray" data-id="element-1407">
                    ({profile.stats.asRenter.reviews}) as borrower
                  </span>
                </div>
                <div className="flex items-center gap-1" data-id="element-1408">
                  <div className="flex" data-id="element-1409">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} className="fill-clay text-clay" data-id="element-1410" />)}
                  </div>
                  <span className="text-[11px] text-moss-gray" data-id="element-1411">
                    ({profile.stats.asLender.reviews}) as lender
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-3 mt-2" data-id="element-1412">
          <SegmentedControl options={['Listings', 'Reviews']} selected={activeTab} onChange={setActiveTab} data-id="element-1413" />
        </div>

        {/* Tab Content */}
        <div className="mt-2 pb-4" data-id="element-1414">
          <AnimatePresence mode="wait" data-id="element-1415">
            {activeTab === 'Listings' ? <motion.div key="listings" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-2 px-1.5" data-id="element-1416">
                {myListings.map(listing => <div key={listing.id} onClick={() => onItemClick(listing.id)} className="bg-white border border-sand rounded-lg p-2 flex gap-2 h-[100px] relative cursor-pointer active:scale-[0.99] transition-transform" data-id="element-1417">
                    {/* Edit Icon on Card */}
                    <button onClick={e => {
                e.stopPropagation();
                // Navigate to edit listing
                onNavigateToInventory();
              }} className="absolute top-2 right-2 p-1 text-moss-gray hover:text-dark-green z-10" data-id="element-1418">
                      <Edit2 size={16} data-id="element-1419" />
                    </button>

                    {/* Image */}
                    <img src={listing.images[0]} alt={listing.title} className="w-20 h-20 rounded-md object-cover flex-shrink-0" data-id="element-1420" />

                    {/* Info */}
                    <div className="flex-1 min-w-0 pt-0.5" data-id="element-1421">
                      <div className="flex justify-between items-start pr-6" data-id="element-1422">
                        <h3 className="text-[13px] font-bold text-charcoal truncate" data-id="element-1423">
                          {listing.title}
                        </h3>
                        <div className="text-[11px] text-charcoal font-medium whitespace-nowrap ml-2" data-id="element-1424">
                          ${listing.pricing.day} per day
                        </div>
                      </div>

                      <div className="flex items-center gap-1 mt-1" data-id="element-1425">
                        <div className="flex" data-id="element-1426">
                          {[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} className={`${i <= Math.round(listing.rating) ? 'fill-clay text-clay' : 'text-sand'}`} data-id="element-1427" />)}
                        </div>
                        <span className="text-[11px] text-moss-gray" data-id="element-1428">
                          ({listing.reviewCount})
                        </span>
                      </div>

                      <div className="mt-auto pt-3 flex items-center gap-1 text-[10px] text-moss-gray" data-id="element-1429">
                        <MapPin size={10} data-id="element-1430" />
                        <span data-id="element-1431">{listing.location}</span>
                      </div>
                    </div>
                  </div>)}

                {/* Add New Listing Button */}
                <button onClick={onCreateListing} className="w-full py-3 border-2 border-dashed border-sand rounded-lg text-moss-gray text-sm font-medium hover:border-dark-green hover:text-dark-green transition-colors flex items-center justify-center gap-2" data-id="element-1432">
                  <PlusCircleIcon size={16} data-id="element-1433" />
                  Add New Listing
                </button>
              </motion.div> : <motion.div key="reviews" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-2 px-1.5" data-id="element-1434">
                {myReviews.map(review => <div key={review.id} className="bg-white border border-sand rounded-lg p-3" data-id="element-1435">
                    <div className="flex justify-between items-start mb-1" data-id="element-1436">
                      <span className="text-xs font-bold text-charcoal" data-id="element-1437">
                        {review.author}
                      </span>
                      <span className="text-[10px] text-moss-gray" data-id="element-1438">
                        {review.date}
                      </span>
                    </div>
                    <div className="flex mb-1" data-id="element-1439">
                      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={10} className={`${i <= review.rating ? 'fill-clay text-clay' : 'text-sand'}`} data-id="element-1440" />)}
                    </div>
                    <p className="text-xs text-charcoal/80 leading-relaxed" data-id="element-1441">
                      {review.text}
                    </p>
                  </div>)}
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Menu Section (Compact) */}
        <div className="px-3 mt-4 mb-6" data-id="element-1442">
          <h3 className="text-xs font-bold text-moss-gray uppercase tracking-wider mb-2 px-1" data-id="element-1443">
            Account Menu
          </h3>
          <div className="bg-white rounded-lg border border-sand divide-y divide-sand overflow-hidden" data-id="element-1444">
            {menuItems.map(item => {
            const Icon = item.icon;
            return <button key={item.id} onClick={item.onClick} className={`w-full flex items-center justify-between p-3 hover:bg-sand/10 transition-colors ${item.variant === 'danger' ? 'text-red-600' : 'text-charcoal'}`} data-id="element-1445">
                  <div className="flex items-center gap-3" data-id="element-1446">
                    <Icon size={16} data-id="element-1447" />
                    <span className="text-sm font-medium" data-id="element-1448">{item.label}</span>
                  </div>
                  <ChevronRight size={16} className="text-moss-gray" data-id="element-1449" />
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Profile" size="lg" footer={<div className="flex gap-3" data-id="element-1451">
            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="flex-1" data-id="element-1452">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveProfile} loading={isSaving} className="flex-1" data-id="element-1453">
              Save Changes
            </Button>
          </div>} data-id="element-1450">
        <div className="space-y-6" data-id="element-1454">
          <AvatarSelector selectedAvatar={editForm.avatar} onSelect={avatarUrl => setEditForm({
          ...editForm,
          avatar: avatarUrl
        })} data-id="element-1455" />

          <div className="space-y-4" data-id="element-1456">
            <Input label="Full Name" value={editForm.name} onChange={value => setEditForm({
            ...editForm,
            name: value
          })} maxLength={NAME_MAX_LENGTH} showCharCount data-id="element-1457" />

            <div data-id="element-1458">
              <div className="flex items-center justify-between mb-2" data-id="element-1459">
                <label className="block text-sm font-medium text-charcoal" data-id="element-1460">
                  Bio
                </label>
                <span className={`text-xs ${editForm.bio.length >= BIO_MAX_LENGTH ? 'text-red-500 font-medium' : editForm.bio.length >= BIO_MAX_LENGTH * 0.9 ? 'text-clay' : 'text-moss-gray'}`} data-id="element-1461">
                  {editForm.bio.length}/{BIO_MAX_LENGTH}
                </span>
              </div>
              <textarea value={editForm.bio} onChange={e => {
              if (e.target.value.length <= BIO_MAX_LENGTH) {
                setEditForm({
                  ...editForm,
                  bio: e.target.value
                });
              }
            }} maxLength={BIO_MAX_LENGTH} rows={3} className="w-full px-4 py-3 bg-cream rounded-ios text-charcoal border-none focus:ring-2 focus:ring-dark-green resize-none" data-id="element-1462" />
            </div>

            {/* Verification Status Section */}
            <div className="space-y-3 pt-2" data-id="element-1463">
              <label className="block text-sm font-medium text-charcoal" data-id="element-1464">
                Verification Status
              </label>

              <div className={`p-4 rounded-ios ${editForm.verified.phone && editForm.verified.id ? 'bg-light-green/30 border border-dark-green/20' : 'bg-sand/30 border border-sand'}`} data-id="element-1465">
                <div className="flex items-center gap-3" data-id="element-1466">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${editForm.verified.phone && editForm.verified.id ? 'bg-dark-green' : 'bg-moss-gray/30'}`} data-id="element-1467">
                    <Shield size={20} className={editForm.verified.phone && editForm.verified.id ? 'text-white' : 'text-moss-gray'} data-id="element-1468" />
                  </div>
                  <div data-id="element-1469">
                    <div className={`font-semibold ${editForm.verified.phone && editForm.verified.id ? 'text-dark-green' : 'text-charcoal'}`} data-id="element-1470">
                      {editForm.verified.phone && editForm.verified.id ? 'Fully Verified' : 'Not Fully Verified'}
                    </div>
                    <div className="text-xs text-moss-gray" data-id="element-1471">
                      {editForm.verified.phone && editForm.verified.id ? 'Your identity has been confirmed' : 'Complete verification to build trust'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2" data-id="element-1472">
                <div className="flex items-center justify-between p-3 bg-cream rounded-ios" data-id="element-1473">
                  <div className="flex items-center gap-2" data-id="element-1474">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${editForm.verified.phone ? 'bg-dark-green' : 'bg-gray-300'}`} data-id="element-1475">
                      {editForm.verified.phone && <CheckCircle2 size={12} className="text-white" data-id="element-1476" />}
                    </div>
                    <span className="text-sm text-charcoal" data-id="element-1477">Phone Number</span>
                  </div>
                  <span className={`text-xs font-medium ${editForm.verified.phone ? 'text-dark-green' : 'text-moss-gray'}`} data-id="element-1478">
                    {editForm.verified.phone ? 'Verified' : 'Not verified'}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-cream rounded-ios" data-id="element-1479">
                  <div className="flex items-center gap-2" data-id="element-1480">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${editForm.verified.id ? 'bg-dark-green' : 'bg-gray-300'}`} data-id="element-1481">
                      {editForm.verified.id && <CheckCircle2 size={12} className="text-white" data-id="element-1482" />}
                    </div>
                    <span className="text-sm text-charcoal" data-id="element-1483">Government ID</span>
                  </div>
                  <span className={`text-xs font-medium ${editForm.verified.id ? 'text-dark-green' : 'text-moss-gray'}`} data-id="element-1484">
                    {editForm.verified.id ? 'Verified' : 'Not verified'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>;
}
function PlusCircleIcon({
  size
}: {
  size: number;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1485">
      <circle cx="12" cy="12" r="10" data-id="element-1486" />
      <path d="M8 12h8" data-id="element-1487" />
      <path d="M12 8v8" data-id="element-1488" />
    </svg>;
}